// src/AdminTeacherScheduledVisits.js
import React, { useEffect, useState } from 'react';
import API from './api';

function StatusBadge({ status }) {
  if (!status) return <span className="text-xs text-gray-500">—</span>;
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-100 text-green-800',
    canceled: 'bg-red-100 text-red-700',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function ReassignedBadge({ value }) {
  if (!value || value === 'none') return null;
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-800',
  };
  const cls = map[value] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {value}
    </span>
  );
}

function TypeBadge({ type }) {
  const isEvent = type === 'event';
  const cls = isEvent
    ? 'bg-orange-100 text-orange-800'
    : 'bg-green-100 text-green-800';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {isEvent ? 'Event' : 'Standard'}
    </span>
  );
}

function ActionBadge({ action }) {
  if (!action) return null;
  const cls =
    action === 'created'
      ? 'bg-sky-100 text-sky-800'
      : 'bg-purple-100 text-purple-800';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {action}
    </span>
  );
}

export default function AdminTeacherScheduledVisits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [teachers, setTeachers] = useState([]);

  // filteri
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignedTeacherId, setAssignedTeacherId] = useState('');
  const [scheduledByTeacherId, setScheduledByTeacherId] = useState('');
  const [status, setStatus] = useState('');

  const [error, setError] = useState('');

  useEffect(() => {
    // sve active teachere za dropdown
    API.get('/teachers')
      .then(res => setTeachers(res.data || []))
      .catch(() => {});
  }, []);

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        teacher_id: assignedTeacherId || undefined,
        scheduled_by_teacher_id: scheduledByTeacherId || undefined,
        status: status || undefined,
      };
      const res = await API.get('/admin/teacher-scheduled-visits', { params });
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }

  // inicijalni load – poslednjih 30 dana bez filtera
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-4">
        Teacher-scheduled visits (overview)
      </h2>

      {/* Filter panel */}
      <div className="mb-4 p-3 border rounded bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1">
              Date from
            </label>
            <input
              type="date"
              className="border rounded px-2 py-1 w-full text-sm"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">
              Date to
            </label>
            <input
              type="date"
              className="border rounded px-2 py-1 w-full text-sm"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              Assigned teacher
            </label>
            <select
              className="border rounded px-2 py-1 w-full text-sm"
              value={assignedTeacherId}
              onChange={e => setAssignedTeacherId(e.target.value)}
            >
              <option value="">All</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              Scheduled by teacher
            </label>
            <select
              className="border rounded px-2 py-1 w-full text-sm"
              value={scheduledByTeacherId}
              onChange={e => setScheduledByTeacherId(e.target.value)}
            >
              <option value="">All</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              Status
            </label>
            <select
              className="border rounded px-2 py-1 w-full text-sm"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="draft">draft</option>
              <option value="complete">complete</option>
              <option value="canceled">canceled</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            onClick={fetchData}
            className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
          <button
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setAssignedTeacherId('');
              setScheduledByTeacherId('');
              setStatus('');
              fetchData();
            }}
            className="px-3 py-1.5 rounded border text-sm"
            disabled={loading}
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="mt-2 text-sm text-red-600">
            {error}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-2 py-2 text-left">Requested at</th>
              <th className="px-2 py-2 text-left">Visit time</th>
              <th className="px-2 py-2 text-left">Student</th>
              <th className="px-2 py-2 text-left">Assigned teacher</th>
              <th className="px-2 py-2 text-left">Scheduled by</th>
              <th className="px-2 py-2 text-left">Type</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Reassigned</th>
              <th className="px-2 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-4 text-center text-gray-500"
                >
                  No teacher-scheduled visits found for current filters.
                </td>
              </tr>
            )}

            {rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-gray-50">
                <td className="px-2 py-1 align-top">
                  <div className="text-xs text-gray-700">
                    {r.requested_at?.replace('T', ' ').slice(0, 16) || r.requested_at}
                  </div>
                </td>
                <td className="px-2 py-1 align-top">
                  <div className="text-xs font-mono">
                    {r.scheduled_at}
                  </div>
                </td>
                <td className="px-2 py-1 align-top">
                  <div className="text-xs font-semibold">
                    {r.student_last_name}, {r.student_first_name}
                  </div>

                </td>
                <td className="px-2 py-1 align-top">
                  <div className="text-xs">
                    {r.assigned_teacher_name}
                  </div>
                </td>
                <td className="px-2 py-1 align-top">
                  <div className="text-xs">
                    {r.scheduled_by_teacher_name}
                  </div>
                </td>
                <td className="px-2 py-1 align-top">
                  <TypeBadge type={r.type} />
                </td>
                <td className="px-2 py-1 align-top">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-2 py-1 align-top">
                  <ReassignedBadge value={r.reassigned_status} />
                </td>
                <td className="px-2 py-1 align-top">
                  <ActionBadge action={r.action} />
                </td>
              </tr>
            ))}

            {loading && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-3 text-center text-gray-500 text-sm"
                >
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

